import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { Auth } from "app/js/src/infrastructure/auth";
import { HttpService } from "app/js/src/infrastructure/http/http";
import { environment } from "app/environments/environment";
import { IClippingStudioFindClipRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-find-clip-repository";
import {
  FindClipRequest,
  FindClipSuggestedClip,
  FindClipSuggestedEdit,
} from "app/js/src/domain/clipping-studio/clipping-studio-find-clip";

const FIND_CLIPS_URL = `${environment.ENDPOINT}/api/AiAssistant/find-clips`;

/** Timestamp HH:MM:SS or H:MM:SS (e.g. 00:00:09). */
const TIMESTAMP_REGEX = /^\d{1,2}:\d{1,2}:\d{1,2}$/;
/** Duration e.g. "10s" or "1m 30s". */
const DURATION_REGEX = /^(\d+)s$|^(\d+)m\s*(\d+)s$/;

/** Backend prompt item (camelCase or PascalCase from API). */
interface FindClipPromptItem {
  text?: string;
  Text?: string;
  created_at?: string;
  CreatedAt?: string;
}

@Injectable()
export class ClippingStudioFindClipRepositoryService extends IClippingStudioFindClipRepository {
  constructor(
    private http: HttpClient,
    private auth: Auth,
    private httpService: HttpService,
  ) {
    super();
  }

  private getHeaders(): HttpHeaders {
    const token = this.auth.token;
    let headers = new HttpHeaders({ "Content-Type": "application/json" });
    if (token) {
      headers = headers.set(
        "Authorization",
        `${token.tokenType} ${token.token}`,
      );
    }
    return headers;
  }

  async generateClips(req: FindClipRequest): Promise<FindClipSuggestedClip[]> {
    if (!req.videoName?.trim()) {
      return [];
    }
    const body = {
      description: req.description ?? "",
      duration: req.duration ?? "30s",
      videoName: req.videoName.trim(),
    };
    const response = await firstValueFrom(
      this.http.post(FIND_CLIPS_URL, body, {
        headers: this.getHeaders(),
        responseType: "text",
      }),
    );
    if (!response) return [];
    const parsed = this.parseFindClipsResponse(response);
    const clips: FindClipSuggestedClip[] = [];
    for (const item of parsed) {
      const normalized = ClippingStudioFindClipRepositoryService.normalizeClip(item);
      if (normalized) clips.push(normalized);
    }
    return clips;
  }

  /**
   * Parses full or truncated JSON array of clips. On full parse failure, tries to recover
   * a partial array by finding the last complete object (ignoring `}` inside strings).
   */
  private parseFindClipsResponse(response: string): unknown[] {
    try {
      const parsed = JSON.parse(response);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return this.tryParsePartialFindClipsJson(response);
    }
  }

  /**
   * Finds indices of closing braces `}` that are not inside a double-quoted string.
   * Used to detect object boundaries in truncated JSON.
   */
  private getStructuralClosingBraceIndices(text: string): number[] {
    const indices: number[] = [];
    let i = 0;
    const len = text.length;
    while (i < len) {
      const ch = text[i];
      if (ch === '"') {
        i++;
        while (i < len) {
          const c = text[i];
          if (c === "\\") {
            i += 2;
            continue;
          }
          if (c === '"') {
            i++;
            break;
          }
          i++;
        }
        continue;
      }
      if (ch === "}") {
        indices.push(i);
      }
      i++;
    }
    return indices;
  }

  /**
   * Tries to parse a truncated JSON array by closing the string at the last complete object.
   */
  private tryParsePartialFindClipsJson(text: string): unknown[] {
    const trimmed = text.trim();
    if (!trimmed.startsWith("[")) return [];

    const braceIndices = this.getStructuralClosingBraceIndices(trimmed);
    for (let j = braceIndices.length - 1; j >= 0; j--) {
      const endIndex = braceIndices[j];
      const candidate = trimmed.substring(0, endIndex + 1) + "]";
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        continue;
      }
    }
    return [];
  }

  /**
   * Validates and normalizes an item to FindClipSuggestedClip (camelCase).
   * Returns null if the item is missing required fields or has invalid format.
   */
  private static normalizeClip(item: unknown): FindClipSuggestedClip | null {
    if (item == null || typeof item !== "object") return null;
    const o = item as Record<string, unknown>;
    const id = o.id ?? o.Id;
    const title = o.title ?? o.Title;
    const timestamp = o.timestamp ?? o.Timestamp;
    const duration = o.duration ?? o.Duration;
    if (
      typeof id !== "string" ||
      typeof title !== "string" ||
      typeof timestamp !== "string" ||
      typeof duration !== "string"
    ) {
      return null;
    }
    const ts = timestamp.trim();
    const dur = duration.trim();
    if (!TIMESTAMP_REGEX.test(ts) || !DURATION_REGEX.test(dur)) return null;

    const confidenceRaw = o.confidence ?? o.Confidence;
    const confidence =
      typeof confidenceRaw === "number" && Number.isFinite(confidenceRaw)
        ? confidenceRaw
        : 0;

    return {
      id: id.trim(),
      title: title.trim(),
      timestamp: ts,
      duration: dur,
      confidence,
    };
  }

  private static totalSecondsFromClips(clips: FindClipSuggestedClip[]): number {
    return clips.reduce((acc, c) => {
      const m = c.duration.match(/^(\d+)s$/);
      if (m) return acc + parseInt(m[1], 10);
      const mm = c.duration.match(/^(\d+)m\s*(\d+)s$/);
      if (mm) return acc + parseInt(mm[1], 10) * 60 + parseInt(mm[2], 10);
      return acc;
    }, 0);
  }

  buildSuggestedEditFromClips(
    clips: FindClipSuggestedClip[],
  ): FindClipSuggestedEdit {
    const totalSec = ClippingStudioFindClipRepositoryService.totalSecondsFromClips(clips);
    return {
      id: "edit-1",
      title: "Suggested Edit",
      clipsCount: clips.length,
      duration:
        totalSec >= 60
          ? `${Math.floor(totalSec / 60)}m ${totalSec % 60}s`
          : `${totalSec}s`,
    };
  }

  async generateFullEdit(
    req: FindClipRequest,
  ): Promise<FindClipSuggestedEdit> {
    const clips = await this.generateClips(req);
    return this.buildSuggestedEditFromClips(clips);
  }

  async getRecentPrompts(projectId: number): Promise<string[]> {
    if (projectId <= 0) return [];
    try {
      const raw = await this.httpService.get(
        `clipping-studio/projects/${projectId}/find-clip/recent-prompts`,
      );
      const arr = Array.isArray(raw) ? raw : [];
      return (arr as FindClipPromptItem[])
        .map((p) => ((p.text ?? p.Text) ?? "").trim())
        .filter((t) => t.length > 0);
    } catch {
      return [];
    }
  }

  async savePrompt(projectId: number, prompt: string): Promise<void> {
    if (!prompt?.trim() || projectId <= 0) return;
    try {
      await this.httpService.post(
        `clipping-studio/projects/${projectId}/find-clip/prompts`,
        { prompt: prompt.trim() },
      );
    } catch {
      // Ignore; panel can retry on next save
    }
  }
}
